
import { useState } from 'react';
import { toast } from 'sonner';
import { httpClient } from '@/lib/http-client';
import { getEndpointUrl } from '@/lib/api-config';
import { SolicitudDetalle } from './types';

interface CancelResponse {
  id: string;
  estado: SolicitudDetalle['estado'];
  rejectionReason: string | null;
}

interface Props {
  solicitud: SolicitudDetalle;
  onCancelled: (estado: SolicitudDetalle['estado'], rejectionReason: string | null) => void;
}

const formatDeadline = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function CancelAdvanceCard({ solicitud, onCancelled }: Props) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isRequested = solicitud.estado === 'revision';
  const isApproved = solicitud.estado === 'aprobada';
  const windowOpen = !!solicitud.cancelableUntil && new Date(solicitud.cancelableUntil).getTime() > Date.now();
  const canCancel = isRequested || (isApproved && windowOpen);

  if (!canCancel) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const url = getEndpointUrl('adelantos', 'cancelar').replace('{id}', solicitud.id);
      const response = await httpClient.post(url, { reason: reason.trim() || undefined });
      const data = (await response.json()) as CancelResponse;
      toast.success('Solicitud cancelada', { description: 'Revertimos el débito de tu línea.' });
      onCancelled(data.estado, data.rejectionReason);
      setOpen(false);
    } catch (err) {
      toast.error('No pudimos cancelar la solicitud', { description: (err as Error).message || 'Intenta nuevamente.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-6 rounded-xl border border-amber-200 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/30 p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="material-symbols-outlined text-amber-500 text-[22px] mt-0.5">hourglass_top</span>
          <div>
            <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              {isRequested ? 'Puedes cancelar esta solicitud' : 'Ventana de cancelación abierta'}
            </h3>
            <p className="mt-0.5 text-xs text-amber-800 dark:text-amber-300">
              {isRequested
                ? 'Todavía no fue aprobada, así que la puedes cancelar sin costo.'
                : `Si cambiaste de idea, puedes cancelar hasta el ${formatDeadline(solicitud.cancelableUntil!)}. Después del desembolso la cancelación ya no es posible.`}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md border border-amber-400 bg-white px-3 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 dark:bg-slate-900 dark:text-amber-200 dark:hover:bg-slate-800"
        >
          <span className="material-symbols-outlined text-base">cancel</span>
          Cancelar solicitud
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-background p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-foreground">Cancelar solicitud</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {isRequested
                ? 'Vamos a cancelar la solicitud antes de que se apruebe.'
                : 'Vamos a cancelar el adelanto. Si ya aprobamos el crédito, revertimos el débito de tu línea de manera automática.'}
            </p>
            <form onSubmit={handleSubmit} className="mt-4 space-y-3">
              <label className="block text-sm">
                Motivo (opcional)
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Ej. ya no necesito el adelanto…"
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-flux-primary"
                />
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
                >
                  Volver
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-50"
                >
                  {submitting ? 'Cancelando…' : 'Confirmar cancelación'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
